import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useAuthStore } from '../store/useAuthStore';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AudioLines,
  CheckCircle2,
  FileText,
  ListChecks,
  Lock,
  MessageSquarePlus,
  Paperclip,
  Send,
  Sparkles,
  User,
  X,
  XCircle,
} from 'lucide-react';
import { Markdown } from '../components/chat/Markdown';
import { FirmioAiAvatar } from '../components/brand/FirmioAiIcon';
import { Select } from '../components/ui/Select';
import { UploadChipList } from '../components/ui/UploadProgress';
import { useUploadQueue } from '../hooks/useUploadQueue';
import {
  getChatStatus,
  streamChat,
  uploadChatAttachment,
  isSupportedAttachment,
  getConversation,
  MAX_ATTACHMENT_BYTES,
  CHAT_MODELS,
  chatModelsFor,
  isChatModelId,
  initialChatModel,
  chatModelsForTiers,
  chatModelLabel,
  DEFAULT_CHAT_MODEL,
  resolveStoredChatModel,
  toolResultToCard,
  type ChatAttachment,
  type ChatMessage,
  type ChatStatus,
  type ChatModelId,
  type ToolResultCard,
} from '../lib/chatApi';
import { takePendingPrompt } from '../lib/editViaChat';
import { VoiceSuggestionsPanel } from '../components/voice/VoiceSuggestionsPanel';
import { useChatViewportLock } from '../hooks/useChatViewportLock';
import { useSidebar } from '../components/layout/SidebarContext';
import { useTestMode } from '../components/testMode/TestModeContext';
import { usePermissions } from '../hooks/usePermissions';
import { useRefreshChatConversations } from '../hooks/useChatConversations';
import { useI18n } from '../contexts/I18nContext';

/**
 * AI Chat assistant page (CLE-31, CLE-45).
 *
 * A ChatGPT/Claude-style interface. Past conversations (search, pin, rename,
 * delete) are listed by `ChatHistorySidebar`, which replaces the app sidebar on
 * this route — same shell as /team-chat. Which thread is open is therefore not
 * page state but the URL: /chat is a fresh thread, /chat/:id an existing one.
 * Conversations persist server-side markdown-first (CLE-40).
 */

// Draft turns for a thread the server has not assigned an id to yet, so a
// reload on /chat doesn't lose them. Anything with an id is already saved
// server-side and reloads from there.
const SESSION_KEY = 'ccc-chat-history';
// Model preference persists across sessions in localStorage (CLE-46), unlike
// the per-session conversation history above. Key bumped to -v2 (CLE-450) so
// clients that had Haiku saved under the old key pick up the new Sonnet
// default (Haiku fabricated tool results instead of calling tools).
const MODEL_KEY = 'ccc-chat-model-v2';

// Tool name -> friendly label for the activity indicator.
const TOOL_LABELS: Record<string, string> = {
  task_create: 'Se creează sarcina',
  task_update: 'Se actualizează sarcina',
  task_query: 'Se caută sarcini',
  refresh_project_tasks: 'Se reîmprospătează sarcinile proiectului',
  paperclip_create_issue: 'Se creează tichet în Paperclip',
  paperclip_update_issue: 'Se actualizează tichetul în Paperclip',
  kb_read_projects: 'Se citesc proiectele din KB',
  kb_update_project: 'Se actualizează proiectul în KB',
  kb_read_financials: 'Se citesc datele financiare',
  kb_read_people: 'Se citește echipa',
  calendar_get_events: 'Se citește calendarul',
  invoice_save: 'Se salvează factura + se actualizează P&L',
  invoice_mark_paid: 'Se marchează factura ca plătită',
  invoice_query: 'Se caută facturi',
  financial_report: 'Se generează raportul P&L',
  kb_create_project: 'Se creează proiect nou',
  kb_allocate_person: 'Se alocă persoană la proiect',
  kb_lint: 'Se verifică sănătatea KB',
  kb_regenerate: 'Se regenerează datele derivate',
  kb_write_log: 'Se scrie în jurnal',
  resource_list: 'Se caută fișierele',
  resource_read: 'Se citește documentul',
  resource_save_draft: 'Se salvează ciorna în Resources',
  efactura_query: 'Se caută în e-Factura (SPV)',
  email_query: 'Se citește inboxul',
  email_search: 'Se caută în Gmail',
  email_read: 'Se citește mailul',
  email_draft: 'Se scrie ciorna în Gmail',
  efactura_get: 'Se pregătește factura din SPV',
};

const SUGGESTION_KEYS = [
  'chat.suggestion.financial',
  'chat.suggestion.team',
  'chat.suggestion.unpaid',
  'chat.suggestion.payments',
];

function loadHistory(): ChatMessage[] {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as ChatMessage[]) : [];
  } catch {
    return [];
  }
}

/** The saved preference, or null when this browser never picked a model. */
function loadStoredModel(): ChatModelId | null {
  try {
    return resolveStoredChatModel(localStorage.getItem(MODEL_KEY));
  } catch {
    return null;
  }
}

export function ChatPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const refreshConversations = useRefreshChatConversations();
  // The open thread comes from the URL, so the sidebar (rendered outside this
  // page) and the page can't disagree about what is open.
  const { conversationId: routeParam } = useParams<{ conversationId: string }>();
  const conversationId = routeParam ?? null;
  const [messages, setMessages] = useState<ChatMessage[]>(() =>
    conversationId ? [] : loadHistory(),
  );
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [activeTool, setActiveTool] = useState<string | null>(null);
  const [error, setErrorMessage] = useState<string | null>(null);
  // Machine-readable code for the current error (e.g. AI_BUDGET_EXCEEDED links
  // to Settings). Cleared implicitly by every plain setError(...) call.
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const setError = useCallback((message: string | null, code: string | null = null) => {
    setErrorMessage(message);
    setErrorCode(code);
  }, []);
  const [status, setStatus] = useState<ChatStatus | null>(null);
  const storedModel = useRef(loadStoredModel());
  const [model, setModel] = useState<ChatModelId>(storedModel.current ?? DEFAULT_CHAT_MODEL);
  const [attachments, setAttachments] = useState<ChatAttachment[]>([]);
  // One chip per file being attached, named and with its own bar, instead of a
  // single anonymous "Se încarcă…" that hid which file was still going.
  const { uploads, isUploading, run } = useUploadQueue();
  const [dragOver, setDragOver] = useState(false);

  const cancelRef = useRef<(() => void) | null>(null);
  // Which thread the messages on screen belong to. Lets the loader below tell
  // "the user opened a different conversation" (fetch it) apart from "we just
  // adopted the id the server assigned to the thread we already streamed".
  const loadedIdRef = useRef<string | null>(conversationId);
  const messageScrollRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  // The long placeholder wraps to two lines on a phone and doubles the
  // composer's height before a single character is typed.
  const { isMobile } = useSidebar();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Auto-grow the composer as the user types, up to ~5 rows, then show a
  // scrollbar for anything beyond that (CLE-436). We reset the height to
  // `auto` first so the textarea can shrink back down when text is removed
  // or after a send clears the input.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    // Cap growth at the CSS max-height (max-h-[7.25rem] ≈ 5 rows at leading-5
    // + py-2); once scrollHeight exceeds it, overflow-y-auto renders the
    // scrollbar automatically.
    el.style.height = `${el.scrollHeight}px`;
  }, [input]);

  const stop = useCallback(() => {
    cancelRef.current?.();
    cancelRef.current = null;
    setStreaming(false);
    setActiveTool(null);
  }, []);

  // Load assistant availability once.
  useEffect(() => {
    void getChatStatus()
      .then((s) => {
        setStatus(s);
        // Nothing picked in this browser yet: open on the customer's default
        // (set in the Firmio Console), which the persist effect then keeps.
        if (!storedModel.current && s.defaultModel && isChatModelId(s.defaultModel)) {
          setModel(s.defaultModel);
        }
      })
      .catch(() => setStatus({ enabled: false, model: '' }));
  }, []);

  // Follow the URL: load the thread it names, or clear for a fresh one. The
  // ref guard makes re-entering the same conversation a no-op, so adopting the
  // server-assigned id after the first turn does not re-fetch what we just
  // streamed (and does not fight an in-flight stream).
  useEffect(() => {
    if (conversationId === loadedIdRef.current) return;
    loadedIdRef.current = conversationId;
    stop();
    setError(null);
    setAttachments([]);
    if (!conversationId) {
      setMessages([]);
      return;
    }
    let cancelled = false;
    void getConversation(conversationId)
      .then((conversation) => {
        if (cancelled) return;
        setMessages(conversation.turns.map((turn) => ({ role: turn.role, content: turn.content })));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : t('chat.error.load'));
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId, stop, setError, t]);

  // Pre-fill the composer when arriving from an "Edit with AI" action (CLE-41).
  useEffect(() => {
    const pending = takePendingPrompt();
    if (pending) {
      setInput(pending);
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (el) {
          el.focus();
          el.setSelectionRange(el.value.length, el.value.length);
        }
      });
    }
  }, []);

  // Keep an unsaved thread across a reload. Once it has an id the server is the
  // source of truth, and mirroring here would resurrect it on the next /chat.
  useEffect(() => {
    if (conversationId) return;
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(messages));
  }, [messages, conversationId]);

  // Persist the model preference so it survives reloads/new sessions (CLE-46).
  useEffect(() => {
    try {
      localStorage.setItem(MODEL_KEY, model);
    } catch {
      // ignore — preference is best-effort
    }
  }, [model]);

  // Auto-scroll to the latest content as it streams. Scroll the message-list
  // container directly (not scrollIntoView, which can also scroll the document /
  // layout viewport and fight the keyboard pin on iOS — CLE-116).
  useEffect(() => {
    const el = messageScrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, activeTool]);

  // Lock the document to the visible viewport while the chat page is mounted
  // (CLE-120). See the hook for the full rationale.
  useChatViewportLock();

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files);
      if (!list.length) return;
      setError(null);
      for (const file of list) {
        if (!isSupportedAttachment(file)) {
          setError(t('chat.error.unsupported', { name: file.name }));
          continue;
        }
        if (file.size > MAX_ATTACHMENT_BYTES) {
          setError(t('chat.error.tooLarge', { name: file.name }));
          continue;
        }
        try {
          const att = await run(file, (onProgress) => uploadChatAttachment(file, onProgress));
          setAttachments((prev) => [...prev, att]);
        } catch (err) {
          setError(err instanceof Error ? err.message : t('chat.error.upload'));
        }
      }
    },
    [t, run],
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (e.dataTransfer?.files?.length) void handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  const removeAttachment = (id: string) =>
    setAttachments((prev) => prev.filter((a) => a.id !== id));

  const focusComposer = useCallback(() => {
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, []);

  const send = useCallback(() => {
    const text = input.trim();
    if ((!text && attachments.length === 0) || streaming || isUploading) return;

    setError(null);
    const sentAttachments = attachments;
    const display =
      text ||
      `(${t(sentAttachments.length === 1 ? 'chat.attachedFile' : 'chat.attachedFiles', {
        count: sentAttachments.length,
      })})`;
    const userMsg: ChatMessage = { role: 'user', content: display };
    const history = [...messages, userMsg];
    setMessages([...history, { role: 'assistant', content: '' }]);
    setInput('');
    setAttachments([]);
    setStreaming(true);

    cancelRef.current = streamChat(
      // Drop empty turns (an interrupted stream can leave an empty assistant
      // placeholder behind) — the backend rejects empty message content.
      [
        ...messages,
        { role: 'user' as const, content: text || t('chat.processAttachments') },
      ].filter((m) => m.content.trim() !== ''),
      {
        onDelta: (chunk) => {
          setMessages((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last && last.role === 'assistant') {
              next[next.length - 1] = { ...last, content: last.content + chunk };
            }
            return next;
          });
        },
        onReplace: (text) => {
          setMessages((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last && last.role === 'assistant')
              next[next.length - 1] = { ...last, content: text };
            return next;
          });
        },
        onModel: ({ model: answering, auto }) => {
          if (!auto) return;
          setMessages((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last && last.role === 'assistant') {
              next[next.length - 1] = {
                ...last,
                autoModels: [...(last.autoModels ?? []), answering],
              };
            }
            return next;
          });
        },
        onToolUse: (name) => setActiveTool(name),
        onToolResult: (name, result) => {
          setActiveTool(null);
          // Attach an authoritative card built from the REAL backend result to
          // the current assistant turn, so a write's ground truth is always
          // visible regardless of what the model narrates (CLE-438).
          const card = toolResultToCard(name, result);
          if (!card) return;
          setMessages((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last && last.role === 'assistant') {
              next[next.length - 1] = {
                ...last,
                toolResults: [...(last.toolResults ?? []), card],
              };
            }
            return next;
          });
        },
        onError: (message, code) => setError(message, code ?? null),
        onDone: (_stopReason, newConversationId) => {
          setStreaming(false);
          setActiveTool(null);
          cancelRef.current = null;
          // Adopt the server-assigned thread id so the next turn continues it,
          // and so a reload lands back on this conversation. `replace` keeps
          // Back pointing at wherever the user came from, not at the empty
          // /chat they just typed into.
          if (newConversationId && newConversationId !== conversationId) {
            loadedIdRef.current = newConversationId;
            navigate(`/chat/${newConversationId}`, { replace: true });
            sessionStorage.removeItem(SESSION_KEY);
          }
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            // Drop a truly empty assistant turn, but keep one that carries
            // authoritative tool-result cards even if it has no prose (CLE-438).
            if (
              last &&
              last.role === 'assistant' &&
              last.content.trim() === '' &&
              !(last.toolResults && last.toolResults.length > 0)
            ) {
              return prev.slice(0, -1);
            }
            return prev;
          });
          // Refresh the sidebar so the new/updated thread shows up.
          refreshConversations();
        },
      },
      sentAttachments,
      conversationId ?? undefined,
      model,
    );
  }, [
    input,
    messages,
    streaming,
    isUploading,
    attachments,
    conversationId,
    model,
    navigate,
    refreshConversations,
    setError,
    t,
  ]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // New Chat: back to the id-less route, which the loader effect reads as
  // "fresh thread". Clearing here as well keeps it instant when we are already
  // on /chat, where there is no navigation for the effect to react to.
  const newChat = useCallback(() => {
    stop();
    sessionStorage.removeItem(SESSION_KEY);
    loadedIdRef.current = null;
    setMessages([]);
    setAttachments([]);
    setError(null);
    navigate('/chat');
    focusComposer();
  }, [stop, navigate, focusComposer, setError]);

  // The demo account can look at the assistant but not talk to it: a live
  // model call is what a paid account buys, and Test Mode could only ever
  // answer with canned text that would misrepresent the product anyway.
  const { enabled: demoMode } = useTestMode();
  // RBAC: aiAssistant.canAccess. The matrix is polled (usePermissions), so a
  // role change made by an admin disables the composer here within a minute
  // and the backend refuses the request at once either way.
  const { can: canPermission, isLoading: permissionsLoading, matrix } = usePermissions();
  const aiDenied = !permissionsLoading && !canPermission('aiAssistant', 'canAccess');
  const composerDisabled = demoMode || aiDenied || (status ? !status.enabled : false);
  // RBAC: aiAssistant.modelAccess is a ceiling on the model tier (the server
  // refuses a hand-picked model above it with 403 MODEL_NOT_ALLOWED). Until
  // the matrix is known every option stays visible; once it is, the picker
  // shrinks to the allowed models and a saved choice above the ceiling falls
  // back to Auto, which the server caps on its own.
  const isAdmin = useAuthStore((s) => s.user?.role === 'ADMIN');
  const modelAccess = isAdmin ? 'both' : matrix?.aiAssistant?.modelAccess;
  const roleOptions = modelAccess ? chatModelsFor(modelAccess) : CHAT_MODELS;
  // The customer's subscription (Firmio Console) narrows the role's list.
  const tierOptions = status?.tiers ? chatModelsForTiers(status.tiers) : null;
  const modelOptions = tierOptions
    ? roleOptions.filter((o) => tierOptions.some((t) => t.id === o.id))
    : roleOptions;
  // One tier only: Auto and that model are the same thing, so no picker.
  const singleModel = modelOptions.length === 2 ? modelOptions[1] : null;
  useEffect(() => {
    if (!modelAccess && !tierOptions) return;
    if (singleModel) {
      if (model !== singleModel.id) setModel(singleModel.id);
    } else if (!modelOptions.some((m) => m.id === model)) {
      setModel(initialChatModel(status, modelOptions));
    }
  }, [modelAccess, tierOptions, modelOptions, model, singleModel, status]);

  // Strict flex column so only the message list scrolls (CLE-106): the
  // header and composer are fixed-size, the message list flexes.
  return (
    <div className="chat-viewport flex w-full min-w-0 flex-col overflow-hidden p-0 md:p-6">
      {/* Header — compact on mobile (CLE-106): icon-only model chip and
          icon-only New Chat so the title stays on one line and nothing wraps
          or truncates. Full labels return at the sm breakpoint. */}
      <div className="mb-2 flex shrink-0 items-center justify-between gap-2 px-4 pt-3 md:mb-4 md:px-0 md:pt-0">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {/* No hamburger here: the conversation list is the app sidebar now,
              opened by the one in the layout's mobile top bar. */}
          <FirmioAiAvatar size={36} />
          <div className="min-w-0">
            <h1 className="truncate text-lg font-bold text-foreground md:text-xl">Firmio AI</h1>
            <p className="hidden truncate text-xs text-muted-foreground sm:block">
              {status?.enabled ? t('chat.modelLabel', { model: status.model }) : t('chat.subtitle')}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {/* Model selector (CLE-46): Haiku / Sonnet (default) / Opus, persisted
              in localStorage. Applies instantly to the next message sent. */}
          <div
            className="flex items-center gap-1.5 text-sm text-muted-foreground"
            title={t('chat.modelTitle')}
          >
            <Sparkles size={15} className="shrink-0 text-primary" />
            {singleModel ? (
              // The subscription leaves one model: say which, offer no choice.
              <span className="rounded-md border border-border px-2 py-1 text-xs text-foreground">
                {singleModel.label}
              </span>
            ) : (
              <Select
                label={t('chat.modelAria')}
                hideLabel
                size="sm"
                value={model}
                onChange={(v) => setModel(v as ChatModelId)}
                options={modelOptions.map((m) => ({
                  value: m.id,
                  label: m.label,
                  description: m.description,
                }))}
                className="w-28 sm:w-32"
              />
            )}
          </div>
          {/* Firmio Voice is the assistant's voice mode: same brain, same
              history — the conversation lands back here. */}
          <Link
            to="/voice"
            aria-label={t('chat.voiceMode')}
            title={t('chat.voiceMode')}
            className="touch-target-auto flex h-[38px] items-center gap-1.5 rounded-md border border-primary/40 bg-primary/10 px-2 text-sm text-primary hover:bg-primary/20 sm:px-3"
          >
            <AudioLines size={15} />
            <span className="hidden sm:inline">{t('chat.voiceMode')}</span>
          </Link>
          <button
            type="button"
            onClick={newChat}
            aria-label={t('chat.newConversation')}
            title={t('chat.newConversation')}
            className="touch-target-auto flex h-[38px] items-center gap-1.5 rounded-md border border-border px-2 text-sm text-muted-foreground hover:bg-accent hover:text-foreground sm:px-3"
          >
            <MessageSquarePlus size={15} />
            <span className="hidden sm:inline">{t('chat.newConversation')}</span>
          </button>
        </div>
      </div>

      {/* Demo account: the composer below is disabled, and this says why. */}
      {demoMode && (
        <div className="mx-4 mb-2 flex shrink-0 items-start gap-2.5 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-foreground md:mx-0 md:mb-4">
          <Lock size={16} className="mt-0.5 shrink-0 text-amber-600" />
          <span>{t('chat.demoLocked')}</span>
        </div>
      )}

      {/* Not-configured notice */}
      {status && !status.enabled && !demoMode && (
        <div className="mx-4 mb-2 shrink-0 rounded-lg border border-border bg-card p-4 text-sm text-card-foreground md:mx-0 md:mb-4">
          {t('chat.notConfigured.before')} <code className="font-mono">ANTHROPIC_API_KEY</code>{' '}
          {t('chat.notConfigured.after')}
        </div>
      )}

      {/* Drafts proposed by Firmio Voice during this conversation (tasks,
          reminders, Meet meetings) — nothing exists until accepted here. */}
      {conversationId && !demoMode && <VoiceSuggestionsPanel conversationId={conversationId} />}

      {/* Message list — the only scrollable region (CLE-106). When empty, the
          inner container fills the available height and centers the welcome
          card so there is no large empty gap above the composer. */}
      <div
        ref={messageScrollRef}
        className="mx-4 min-h-0 flex-1 overflow-y-auto rounded-lg border border-border bg-background md:mx-0"
      >
        <div
          className={`w-full p-4 md:px-6 ${
            messages.length === 0 ? 'flex min-h-full flex-col justify-center' : 'space-y-4'
          }`}
        >
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center text-center">
              <FirmioAiAvatar size={48} className="mb-3 rounded-xl" />
              <p className="text-sm text-muted-foreground">{t('chat.intro')}</p>
              <div className="mt-4 grid w-full max-w-md gap-2 sm:grid-cols-2">
                {SUGGESTION_KEYS.map((key) => {
                  const label = t(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      disabled={demoMode}
                      onClick={() => setInput(label)}
                      className="rounded-md border border-border bg-card px-3 py-2 text-left text-sm text-card-foreground hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-card"
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {messages.map((msg, idx) => {
            // While streaming, the last assistant turn starts empty. Rather
            // than render an empty bordered bubble (and a separate spinner
            // below it), show a lively "thinking" bubble in its place until
            // the first token — or a tool card — arrives (CLE-435).
            const isLast = idx === messages.length - 1;
            const isPendingAssistant =
              isLast &&
              streaming &&
              msg.role === 'assistant' &&
              !msg.content &&
              !(msg.toolResults && msg.toolResults.length > 0);
            if (isPendingAssistant) {
              return (
                <ThinkingIndicator
                  key={idx}
                  label={activeTool ? (TOOL_LABELS[activeTool] ?? activeTool) : null}
                />
              );
            }
            return <MessageBubble key={idx} message={msg} />;
          })}

          {aiDenied && (
            <div className="rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
              {t('chat.noAccess')}
            </div>
          )}

          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
              {errorCode === 'AI_BUDGET_EXCEEDED' && (
                <Link to="/settings" className="ml-2 font-medium underline underline-offset-2">
                  {t('chat.budgetExceededLink')}
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Composer — fixed-size flex child that is never compressed
          (shrink-0) so it stays fully visible at the bottom of the viewport
          (CLE-106). `chat-composer-pad` (index.css) clears the iPhone home
          indicator via the safe-area inset (CLE-91) — and drops it while the
          keyboard is up, when the indicator is under the keyboard and the
          inset only left a dead band above it. */}
      <div className="chat-composer-pad shrink-0 bg-background px-4 pt-2 md:mt-4 md:bg-transparent md:px-0 md:pb-0 md:pt-0">
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,image/png,image/jpeg,image/jpg,image/webp,.docx,.xlsx,.doc,.xls,.csv,.txt,.md"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) void handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <div
          onDragOver={(e) => {
            e.preventDefault();
            if (!composerDisabled) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={composerDisabled ? undefined : onDrop}
          className={`w-full rounded-lg border bg-card p-2 transition-colors ${
            dragOver ? 'border-primary border-dashed bg-accent/50' : 'border-border'
          }`}
        >
          {/* Attachment chips */}
          {(attachments.length > 0 || isUploading) && (
            <div className="mb-2 flex flex-wrap items-center gap-2 px-1">
              {attachments.map((a) => (
                <span
                  key={a.id}
                  className="flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1 text-xs text-foreground"
                >
                  <FileText size={13} className="text-muted-foreground" />
                  <span className="max-w-[12rem] truncate">{a.fileName}</span>
                  <button
                    type="button"
                    onClick={() => removeAttachment(a.id)}
                    aria-label={t('chat.remove')}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X size={13} />
                  </button>
                </span>
              ))}
              <UploadChipList uploads={uploads} />
            </div>
          )}

          <div className="flex items-start gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={composerDisabled || streaming}
              aria-label={t('chat.attach')}
              title={t('chat.attachTitle')}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40"
            >
              <Paperclip size={18} />
            </button>
            {/* `text-base` (16px) on mobile is load-bearing, not a style
                choice: iOS Safari auto-zooms into any focused field smaller
                than 16px. That zoom shrinks the visual viewport — which
                `--app-height` sizes the entire shell to — and pans the page
                to the caret, so the composer flew off-screen and stayed
                unreachable until the user pinched back out. The CLE-116/120
                backstop cannot undo it: the pan is a visual-viewport offset,
                not a document scroll. Back to `text-sm` from md up, where no
                auto-zoom exists. */}
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                demoMode
                  ? t('chat.demoPlaceholder')
                  : isMobile
                    ? t('chat.placeholderMobile')
                    : t('chat.placeholder')
              }
              rows={1}
              disabled={composerDisabled}
              className="max-h-[7.25rem] min-h-[2.5rem] flex-1 resize-none overflow-y-auto bg-transparent px-2 py-2 text-base leading-6 text-foreground outline-none placeholder:text-muted-foreground disabled:opacity-50 md:text-sm md:leading-5"
            />
            {streaming ? (
              <button
                type="button"
                onClick={stop}
                className="flex h-10 items-center justify-center rounded-md border border-border px-3 text-sm text-muted-foreground hover:bg-accent"
              >
                {t('chat.stop')}
              </button>
            ) : (
              <button
                type="button"
                onClick={send}
                disabled={
                  (!input.trim() && attachments.length === 0) || composerDisabled || isUploading
                }
                className="flex h-10 w-10 items-center justify-center rounded-md bg-primary text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-40"
                aria-label={t('chat.send')}
              >
                <Send size={18} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Your profile photo on your own bubbles; the generic glyph until you set one. */
function OwnBubbleAvatar() {
  const avatarUrl = useAuthStore((s) => s.user?.avatarUrl);
  if (avatarUrl) {
    return <img src={avatarUrl} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" />;
  }
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent text-foreground">
      <User size={16} />
    </div>
  );
}

function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user';
  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      {isUser ? <OwnBubbleAvatar /> : <FirmioAiAvatar size={32} />}
      <div
        className={`min-w-0 rounded-lg px-4 py-2 ${
          isUser
            ? 'max-w-[75%] bg-primary text-primary-foreground'
            : 'w-full flex-1 border border-border bg-card text-card-foreground'
        }`}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap text-sm">{message.content}</p>
        ) : (
          <>
            <Markdown content={message.content} />
            {message.toolResults && message.toolResults.length > 0 && (
              <div className="mt-2 flex flex-col gap-2">
                {message.toolResults.map((card, i) => (
                  <ToolResultCardView key={i} card={card} />
                ))}
              </div>
            )}
            {message.autoModels && message.autoModels.length > 0 && (
              <p className="mt-1 text-xs text-muted-foreground">
                Auto · {message.autoModels.map(chatModelLabel).join(' → ')}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Pending-assistant "thinking" bubble (CLE-435).
 *
 * Shown in place of the empty assistant bubble while the AI composes its reply.
 * A trio of gradient dots bounce in sequence (CSS `.chat-thinking`) so the wait
 * reads as active rather than a blank placeholder with a spinner underneath.
 * `label` is the current tool's status text, or null while the assistant is
 * merely composing — the bouncing dots already say "working", so a generic
 * "typing…" caption would only repeat them.
 */
function ThinkingIndicator({ label }: { label: string | null }) {
  return (
    <div className="flex gap-3">
      <FirmioAiAvatar size={32} />
      <div className="flex min-w-0 items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 text-card-foreground">
        <span className="chat-thinking" aria-hidden="true">
          <span className="chat-thinking-dot" />
          <span className="chat-thinking-dot" />
          <span className="chat-thinking-dot" />
        </span>
        {label && <span className="text-xs text-muted-foreground">{label}…</span>}
      </div>
    </div>
  );
}

/**
 * Authoritative card rendered from the REAL backend tool result (CLE-438).
 * This is the source of truth the user can trust: it exists only when the
 * backend actually returned a result, so a model that fabricates a "created"
 * message in prose produces no card, and a genuine create always shows one.
 */
function ToolResultCardView({ card }: { card: ToolResultCard }) {
  if (!card.ok) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
        <XCircle size={14} className="mt-0.5 shrink-0" />
        <span>{card.error ?? 'Operatia a esuat.'}</span>
      </div>
    );
  }

  if (card.tool === 'task_create' || card.tool === 'task_update') {
    const verb = card.tool === 'task_create' ? 'Creat' : 'Actualizat';
    return (
      <div className="flex items-start gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-300">
        <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
        <span>
          {verb} in baza de date:{' '}
          {card.identifier ? (
            <Link
              to={`/tasks/${encodeURIComponent(card.identifier)}`}
              className="font-mono font-semibold underline underline-offset-2"
            >
              {card.identifier}
            </Link>
          ) : (
            <span className="font-semibold">(fara identifier)</span>
          )}
          {card.title ? <span className="text-muted-foreground"> — {card.title}</span> : null}
        </span>
      </div>
    );
  }

  if (card.tool === 'task_query') {
    return (
      <div className="flex items-start gap-2 rounded-md border border-border bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
        <ListChecks size={14} className="mt-0.5 shrink-0" />
        <span>
          Interogare in baza de date: <span className="font-semibold">{card.count ?? 0}</span>{' '}
          {card.count === 1 ? 'task gasit' : 'task-uri gasite'}.
        </span>
      </div>
    );
  }

  return null;
}
